import { ICommonQueryParams, IUser } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  assignRole as assignRoleApi,
  createAppRole as createAppRoleApi,
  createUser as createUserApi,
  deleteAppRole as deleteAppRoleApi,
  deleteUser as deleteUserApi,
  getActiveUsersCount as getActiveUsersCountApi,
  getDeactivatedUsersCount as getDeactivatedUsersCountApi,
  getAdministratorsCount as getAdministratorsCountApi,
  getAppRole as getAppRoleApi,
  getAppRoleList as getAppRoleListApi,
  getAssignRole as getAssignRoleApi,
  getAssignRoleList as getAssignRoleListApi,
  getTotalUsersCount as getTotalUsersCountApi,
  getUser as getUserApi,
  getUserList as getUserListApi,
  getUserRoles as getUserRolesApi,
  getUsersCountByTeam as getUsersCountByTeamApi,
  unassignRole as unassignRoleApi,
  updateAppRole as updateAppRoleApi,
  updateAssignRole as updateAssignRoleApi,
  updateUser as updateUserApi,
} from "../api";
import { queryKey } from "../constants";
import { appQueryKey } from "@/utils/constant/appQueryKey";
import {
  IAppRoleForm,
  IAssignRoleForm,
  IGetUserRolesResponse,
  ITransfromUserRolesResponse,
  IUserForm,
  IUserStats,
} from "../types";

// ============================================
// APP ROLE HOOKS
// ============================================

/**
 * Hook to fetch app role list
 */
export const useAppRoleList = ({
  queryParams,
  enabled = true,
}: {
  queryParams?: ICommonQueryParams;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKey.getAppRoleList, queryParams],
    queryFn: () => getAppRoleListApi(queryParams),
    enabled,
  });
};

/**
 * Hook to fetch single app role
 */
export const useAppRole = (id: string, enabled = true) => {
  return useQuery({
    queryKey: [queryKey.getAppRole, id],
    queryFn: () => getAppRoleApi(id),
    enabled: enabled && !!id,
  });
};

/**
 * Hook to create a new app role
 */
export const useCreateAppRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IAppRoleForm) => createAppRoleApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getAppRoleList] });
      toast.success("App role created successfully");
    },
  });
};

/**
 * Hook to update an existing app role
 */
export const useUpdateAppRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { id: string; payload: Partial<IAppRoleForm> }) =>
      updateAppRoleApi(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getAppRoleList] });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAppRole, variables.id],
      });
      toast.success("App role updated successfully");
    },
  });
};

/**
 * Hook to delete an app role
 */
export const useDeleteAppRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAppRoleApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getAppRoleList] });
      toast.success("App role deleted successfully");
    },
  });
};

// ============================================
// ASSIGN ROLE HOOKS
// ============================================

/**
 * Hook to fetch role assignment list
 */
export const useAssignRoleList = ({
  queryParams,
  enabled = true,
}: {
  queryParams?: ICommonQueryParams;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKey.getAssignRoleList, queryParams],
    queryFn: () => getAssignRoleListApi(queryParams),
    enabled,
  });
};

/**
 * Hook to fetch roles assigned to a specific user
 */
export const useUserRoles = (systemuserid: string, enabled = true) => {
  return useQuery({
    queryKey: [queryKey.getUserRoles, systemuserid],
    queryFn: () => getUserRolesApi(systemuserid),
    enabled: enabled && !!systemuserid,
    select: (data: IGetUserRolesResponse): ITransfromUserRolesResponse => {
      const deafultRole = data.data.roles?.find(
        (item) => item.dogma_defaultsecurityrole,
      );
      const customRole = data.data.roles?.find(
        (item) => !item.dogma_defaultsecurityrole,
      );
      return {
        ...data,
        customRole: customRole,
        deafultRole: deafultRole,
      };
    },
  });
};

/**
 * Hook to fetch single role assignment
 */
export const useGetAssignRole = (id: string, enabled = true) => {
  return useQuery({
    queryKey: [queryKey.getAssignRole, id],
    queryFn: () => getAssignRoleApi(id),
    enabled: enabled && !!id,
  });
};

/**
 * Hook to assign a role to a user
 */
export const useAssignRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IAssignRoleForm) => assignRoleApi(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAssignRoleList],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getUserRoles, variables.systemuserid],
      });
      queryClient.invalidateQueries({ queryKey: [queryKey.getUserStats] });
      queryClient.invalidateQueries({ queryKey: [appQueryKey.getCurrentUserDeatils] });
      toast.success("Role assigned successfully");
    },
  });
};

/**
 * Hook to update a role assignment
 */
export const useUpdateAssignRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { id: string; payload: Partial<IAssignRoleForm> }) =>
      updateAssignRoleApi(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAssignRoleList],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAssignRole, variables.id],
      });
      if (variables.payload.systemuserid) {
        queryClient.invalidateQueries({
          queryKey: [queryKey.getUserRoles, variables.payload.systemuserid],
        });
      }
      queryClient.invalidateQueries({ queryKey: [appQueryKey.getCurrentUserDeatils] });
      toast.success("Role assignment updated successfully");
    },
  });
};

/**
 * Hook to unassign a role from a user (delete role assignment)
 */
export const useUnassignRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unassignRoleApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAssignRoleList],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getUserRoles],
      });
      queryClient.invalidateQueries({ queryKey: [queryKey.getUserStats] });
      queryClient.invalidateQueries({ queryKey: [appQueryKey.getCurrentUserDeatils] });
      toast.success("Role unassigned successfully");
    },
  });
};

// ============================================
// USERS HOOKS
// ============================================

/**
 * Hook to fetch user list
 */
export const useUserList = ({
  queryParams,
  enabled = true,
}: {
  queryParams?: ICommonQueryParams;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKey.getUserList, queryParams],
    queryFn: () => getUserListApi(queryParams),
    enabled,
    select: (data) => {
      // Transform each user to include extracted default and custom roles
      const transformedUsers = data.value.map((user) => {
        const defaultRole = user.dogma_AppRole_SystemUser_SystemUser?.find(
          (role) => role.dogma_defaultsecurityrole === true
        );
        const customRole = user.dogma_AppRole_SystemUser_SystemUser?.find(
          (role) => role.dogma_defaultsecurityrole === false
        );

        return {
          ...user,
          defaultRole,
          customRole,
        };
      });

      return {
        "@odata.context": data["@odata.context"],
        "@odata.count": data["@odata.count"],
        "@odata.nextLink": data["@odata.nextLink"],
        "@Microsoft.Dynamics.CRM.totalrecordcountlimitexceeded": data["@Microsoft.Dynamics.CRM.totalrecordcountlimitexceeded"],
        value: transformedUsers,
      };
    },
  });
};

/**
 * Hook to fetch single user
 */
export const useUser = (id: string, enabled = true) => {
  return useQuery({
    queryKey: [queryKey.getUser, id],
    queryFn: () => getUserApi(id),
    enabled: enabled && !!id,
  });
};

/**
 * Hook to create a new user
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IUserForm) => createUserApi(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getUserList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getUserStats] });
      toast.success("User created successfully");
    },
  });
};

/**
 * Hook to update an existing user
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { id: string; payload: Partial<IUser> }) =>
      updateUserApi(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getUserList] });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getUser, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: [queryKey.getUserStats] });
      queryClient.invalidateQueries({ queryKey: [appQueryKey.getCurrentUserDeatils] });
      toast.success("User updated successfully");
    },
  });
};

/**
 * Hook to delete a user
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteUserApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getUserList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getUserStats] });
      toast.success("User deleted successfully");
    },
  });
};

// ============================================
// USER STATS HOOKS
// ============================================

/**
 * Hook to fetch user statistics
 * Fetches total users, active users, administrators, credit control, and sales team counts
 */
export const useUserStats = (region?: number) => {
  return useQuery({
    queryKey: [queryKey.getUserStats, region],
    queryFn: async (): Promise<IUserStats> => {
      const [totalResponse, activeResponse, deactivatedResponse, teamResponse, adminResponse] =
        await Promise.all([
          getTotalUsersCountApi(region),
          getActiveUsersCountApi(region),
          getDeactivatedUsersCountApi(region),
          getUsersCountByTeamApi(region),
          getAdministratorsCountApi(region),
        ]);

      // Calculate Credit Control count (team 2)
      const creditControl =
        teamResponse.value?.find((t) => t.dogma_team === 2)?.count || 0;

      // Calculate Sales Team count (teams 1 and 4)
      const salesTeam =
        teamResponse.value?.reduce((sum, t) => {
          if (t.dogma_team === 1 || t.dogma_team === 4) {
            return sum + (t.count || 0);
          }
          return sum;
        }, 0) || 0;

      return {
        totalUsers: totalResponse.value?.[0]?.count || 0,
        activeUsers: activeResponse.value?.[0]?.count || 0,
        deactivatedUsers: deactivatedResponse.value?.[0]?.count || 0,
        administrators: adminResponse.value?.[0]?.count || 0,
        creditControl,
        salesTeam,
      };
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};
